<script setup lang="ts">
import {computed, onBeforeUnmount, ref} from "vue";
import ChatUtils from "@/utils/common/ChatUtils.ts";
import ChatMediaMenu from "@/components/navBar/chat/ChatMediaMenu.vue";
import {MessageMetadata} from "@/utils/presenters/interfaces.ts";

const props = defineProps<{
  message: MessageMetadata;
  sent: boolean;
  stamp: string;
  darkMode: boolean;
}>();

const fileId = computed(() => props.message.attachment?.attachmentInfo?.fileId);
const downloadUrl = computed(() => ChatUtils.getFileDownloadUrl(fileId.value));
const audioSrc = computed(() => ChatUtils.getFileDownloadUrl(fileId.value));

const audioRef = ref<HTMLAudioElement>();
const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const progress = ref(0);

const BAR_COUNT = 28;

const waveformBars = computed(() => {
  const seed = props.message.id || 'default';
  const bars: number[] = [];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash) + seed.charCodeAt(i);
    hash |= 0;
  }
  for (let i = 0; i < BAR_COUNT; i++) {
    hash = ((hash << 5) - hash) + i;
    hash |= 0;
    bars.push(0.2 + (Math.abs(hash % 100) / 100) * 0.8);
  }
  return bars;
});

function formatTime(seconds: number): string {
  if (!seconds || !isFinite(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const timeDisplay = computed(() => {
  if (isPlaying.value || currentTime.value > 0)
    return formatTime(currentTime.value);
  return formatTime(duration.value);
});

function togglePlay() {
  const audio = audioRef.value;
  if (!audio) return;
  if (isPlaying.value) {
    audio.pause();
  } else {
    audio.play();
  }
}

function onTimeUpdate() {
  const audio = audioRef.value;
  if (!audio) return;
  currentTime.value = audio.currentTime;
  progress.value = audio.duration ? (audio.currentTime / audio.duration) * 100 : 0;
}

function onLoadedMetadata() {
  const audio = audioRef.value;
  if (audio) duration.value = audio.duration;
}

function onPlay() { isPlaying.value = true; }
function onPause() { isPlaying.value = false; }
function onEnded() {
  isPlaying.value = false;
  currentTime.value = 0;
  progress.value = 0;
}

function onBarClick(index: number) {
  const audio = audioRef.value;
  if (!audio || !audio.duration) return;
  const ratio = index / BAR_COUNT;
  audio.currentTime = ratio * audio.duration;
}

onBeforeUnmount(() => {
  audioRef.value?.pause();
});

const bgColor = computed(() => props.sent ? 'tw-bg-[var(--q-primary)]' : (props.darkMode ? 'tw-bg-gray-800' : 'tw-bg-gray-200'));
const textColor = computed(() => (props.sent || props.darkMode) ? 'tw-text-white' : 'tw-text-gray-900');
const barActiveColor = computed(() => (props.sent || props.darkMode) ? 'tw-bg-white' : 'tw-bg-gray-700');
const barInactiveColor = computed(() => (props.sent || props.darkMode) ? 'tw-bg-white/40' : 'tw-bg-gray-400');
</script>

<template>
  <div class="chat-audio-bubble tw-rounded-lg tw-px-3 tw-py-2 tw-min-w-[240px] tw-max-w-[320px]"
       :class="[bgColor, textColor, sent ? 'tw-self-end' : 'tw-self-start']">
    <div class="tw-flex tw-items-center tw-gap-2">
      <button @click="togglePlay"
              class="tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-bg-white/20 hover:tw-bg-white/30 tw-transition-colors">
        <q-icon :name="isPlaying ? 'pause' : 'play_arrow'" size="1.3rem"
                :color="(sent || darkMode) ? 'white' : 'dark'"/>
      </button>
      <div class="tw-flex tw-items-center tw-gap-[2px] tw-flex-1 tw-h-8 tw-cursor-pointer">
        <div v-for="(height, i) in waveformBars" :key="i"
             @click="onBarClick(i)"
             class="tw-w-[3px] tw-rounded-full tw-transition-colors tw-duration-150"
             :class="[(i / BAR_COUNT) * 100 <= progress ? barActiveColor : barInactiveColor]"
             :style="{ height: (height * 28) + 'px' }"/>
      </div>
      <ChatMediaMenu :download-url="downloadUrl"
                     :btn-color="(sent || darkMode) ? 'white' : 'dark'"
                     btn-class="tw-shrink-0 tw-opacity-70 hover:tw-opacity-100"/>
    </div>
    <div class="tw-flex tw-justify-between tw-mt-1 tw-text-[10px] tw-opacity-70">
      <span v-text="timeDisplay"/>
      <span v-text="stamp"/>
    </div>
    <audio ref="audioRef" :src="audioSrc" preload="metadata"
           @timeupdate="onTimeUpdate" @loadedmetadata="onLoadedMetadata"
           @play="onPlay" @pause="onPause" @ended="onEnded"/>
  </div>
</template>
